import { EventEmitter, Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PpHttpService } from '@penpencil/common';
import { PpNotificationService, PpValueItem } from '@penpencil/forms';
import _ from 'lodash';
import { BehaviorSubject, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { ErrorHandlerService } from './error-handler.service';
import { LoadingService } from './loader.service';

@Injectable({
    providedIn: 'root',
})
export class SharedService extends ErrorHandlerService {
    tabIndex: BehaviorSubject<number> = new BehaviorSubject(0);
    isSchool = false;
    school: BehaviorSubject<any> = new BehaviorSubject<any>(null);
    isUploading: BehaviorSubject<any> = new BehaviorSubject<any>(false);

    constructor(
        private _notificationService: PpNotificationService,
        private _ppHttpService: PpHttpService,
        private _loadingService: LoadingService,
    ) {
        super();
    }

    setTabIndex(value: number): void {
        this.tabIndex.next(value);
        localStorage.setItem('activeTab', JSON.stringify(value));
    }

    getTabIndex(): BehaviorSubject<number> {
        return this.tabIndex;
    }

    // NEED TO ADD SUPPORT FOR PDF/EXCEL, ETC.
    checkUploadFileType(
        acceptTypes: string,
        targetFileType: string,
        isImage: boolean = false,
    ): boolean {
        if (acceptTypes?.length) {
            // If input is image, user should not upload non-image file...
            if (isImage && !targetFileType.includes('image')) {
                this._notificationService.showError(
                    `Please provide valid file type. (${acceptTypes})`,
                );
                return false;
            }

            if (acceptTypes.split('/').includes('*')) {
                return true;
            }

            if (!acceptTypes.split(', ').includes(targetFileType)) {
                this._notificationService.showError(
                    `Please provide valid file type. (${acceptTypes})`,
                );
                return false;
            }
            return true;
        }
        return false;
    }

    setSchool(value): void {
        this.school.next(value);
    }

    getSchool(): BehaviorSubject<any> {
        return this.school;
    }

    /**
     * Disable form controls recursively
     * Refer @penpencil/forms Lib for code
     *
     * @param form formGroup/forrmArray/formControl to disable
     * @param disable to enable/disable formControl
     * @return void
     **/
    toggleEnableDisableForm(form: FormGroup, disable: boolean = false): void {
        if (form.controls && Object.keys(form.controls)?.length) {
            Object.keys(form.controls).forEach((formGroup) => {
                const fg = form.controls[formGroup] as FormGroup;
                this.toggleEnableDisableForm(fg, disable);
            });
        } else if (
            form.controls &&
            (form as any as FormArray).controls?.length
        ) {
            (form as any as FormArray).controls.forEach((control) => {
                const fg = control as FormGroup;
                this.toggleEnableDisableForm(fg, disable);
            });
        } else {
            if (disable) {
                (form as AbstractControl).disable();
            } else {
                (form as AbstractControl).enable();
            }
        }
    }

    startUploading(): void {
        this.isUploading.next(true);
    }

    stopUploading(): void {
        this.isUploading.next(false);
    }

    prepareLinkAndNavigate({
        href,
        download,
        target,
    }: {
        href: string;
        download?: string;
        target?: string;
    }): void {
        const aEle: any = document.createElement('a');
        if (href) {
            aEle.href = href;
        }
        if (download) {
            aEle.download = download;
        }
        if (target) {
            aEle.target = '_blank';
        }
        aEle.style = 'display: none';
        document.body.appendChild(aEle);
        aEle.click();
        document.body.removeChild(aEle);
    }

    toggleStatusAPI(apiPath, data): Observable<any> {
        return this._ppHttpService
            .put(data, { apiPath: apiPath })
            .pipe(catchError(this.handleError));
    }

    toggleStatus({
        apiPath,
        event,
        snackBar,
        isFullObject = false,
        refreshEvent,
        activeValue = 'Active',
        inactiveValue = 'Invisible',
    }: {
        apiPath: string;
        event: any;
        snackBar: MatSnackBar;
        isFullObject?: boolean;
        refreshEvent?: EventEmitter<boolean>;
        activeValue?: string;
        inactiveValue?: string;
    }): void {
        const loadingRef = this._loadingService.open();
        event.status =
            event.status === activeValue ? inactiveValue : activeValue;
        this.toggleStatusAPI(
            apiPath,
            isFullObject ? event : { status: event.status },
        ).subscribe(
            () => {
                if (refreshEvent) {
                    refreshEvent.next(true);
                }
                if (loadingRef) {
                    loadingRef.close();
                }
            },
            (err) => {
                if (snackBar) {
                    snackBar.open(
                        err?.message ?? err ?? 'Something went wrong',
                        'OK',
                        {
                            duration: 3000,
                        },
                    );
                }
                if (refreshEvent) {
                    refreshEvent.next(true);
                }
                if (loadingRef) {
                    loadingRef.close();
                }
            },
        );
    }

    copyText({
        text,
        _snackBar,
        successMessage,
    }: {
        text: string;
        _snackBar: MatSnackBar;
        successMessage?: string;
    }) {
        const event = (e: ClipboardEvent) => {
            e.clipboardData.setData('text/plain', text);
            e.preventDefault();
            document.removeEventListener('copy', event);
        };
        document.addEventListener('copy', event);
        document.execCommand('copy');
        if (_snackBar) {
            _snackBar.open(successMessage ?? 'Data copied', 'Ok', {
                duration: 3000,
            });
        }
    }

    generatePassword(): string {
        const chars =
            '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ';
        const passwordLength = 9;
        let password = '';
        for (let i = 0; i <= passwordLength; i++) {
            const randomNumber = Math.floor(Math.random() * chars.length);
            password += chars.substring(randomNumber, randomNumber + 1);
        }
        return password;
    }

    convertUnicodeToEmoji(unicode: string): string {
        return String.fromCodePoint(parseInt(unicode.replace('U+', ''), 16));
    }

    getUnicodeValue(emoji: string): string {
        const codePoints = Array.from(emoji).map((char) =>
            char.codePointAt(0)?.toString(16).toUpperCase(),
        );
        return codePoints ? `U+${codePoints.join(' U+')}` : '';
    }

    createSelection(dropdownValues: string[] | undefined): PpValueItem[] {
        if (dropdownValues?.length) {
            return _.uniqBy(
                dropdownValues.map((el) => ({
                    value: el,
                    viewValue: el,
                })),
                'value',
            );
        }
        return [
            {
                value: '',
                viewValue: '',
            },
        ];
    }

    filterNullValueInArray(data: any[]) {
        return data?.length ? data.filter((item: any) => !!item && item) : [];
    }
}
